import { Star, Quote } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

function Avatar({ name }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#FF5533] to-[#FFB800] text-sm font-semibold text-white"
    >
      {initials}
    </div>
  );
}

export default function Reviews() {
  const { t } = useLanguage();
  const r = t.reviews;

  return (
    <section
      id="reviews"
      className="relative bg-[#0A0A0A] py-20 text-white md:py-28"
      data-testid="reviews-section"
    >
      {/* glow */}
      <div className="pointer-events-none absolute -top-32 right-0 h-[500px] w-[500px] rounded-full bg-[#FF5533]/15 blur-[160px]" />
      <div className="pointer-events-none absolute -bottom-32 left-0 h-[400px] w-[400px] rounded-full bg-[#FFB800]/10 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {/* Heading */}
        <div className="mb-14 grid items-end gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#FFB800]">
              {r.eyebrow}
            </p>
            <h2
              className="text-4xl leading-[1.02] tracking-tight md:text-6xl"
              style={{ fontFamily: "Fraunces, serif" }}
              data-testid="reviews-title"
            >
              {r.title}
            </h2>
          </div>
          <div className="flex flex-col items-start gap-3 md:col-span-5 md:items-end">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  size={20}
                  className="fill-[#FFB800] text-[#FFB800]"
                />
              ))}
              <span
                className="ml-2 text-2xl"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                4.7
              </span>
            </div>
            <p className="text-sm text-white/65">{r.sub}</p>
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJ8Xq3ZI69AowRo-7DDeG4MJI"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-cta"
              className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-white/50 hover:bg-white/10"
            >
              {r.cta} →
            </a>
          </div>
        </div>

        {/* Review grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {r.list.map((review, i) => (
            <article
              key={review.name + i}
              className="group relative flex flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-[#FF5533]/40 hover:bg-white/[0.07]"
              data-testid={`review-card-${i}`}
            >
              <Quote
                size={28}
                strokeWidth={1.5}
                className="text-[#FF5533]/70"
              />
              <p className="text-[15px] leading-relaxed text-white/85">
                &ldquo;{review.quote}&rdquo;
              </p>
              <div className="mt-auto flex items-center gap-3 border-t border-white/10 pt-5">
                <Avatar name={review.name} />
                <div>
                  <div className="text-sm font-semibold text-white">
                    {review.name}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-white/50">
                    {review.when}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={12}
                      className="fill-[#FFB800] text-[#FFB800]"
                    />
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
